import * as THREE from "three";
import {
  ROOM_OUTPUT_IDS,
  ROOM_SOURCE_BUS_ID,
  ROOM_SOURCE_BUS_MAX_OCCUPANTS,
  SWITCH_LOAD_TEST_NODE_ID,
  type RoomOutputId,
  type RoomPanelNodeId,
} from "../electrical/roomCircuit";
import {
  CEILING_LIGHT_A,
  CEILING_LIGHT_B,
  FAN,
  OUTLET_A,
  PANEL,
  SIGN,
  SWITCH,
  WALL_LIGHT,
} from "./workshopLayout";

/**
 * Electrical garage kit — meshes and naming hooks only.
 *
 * Hands / electrical own grab, jumper snap, and station updates. Default
 * circuit: Light A jumper seated srcP ↔ outLightA; other leads parked OPEN.
 *
 * | Object           | name / notes                                              |
 * | ---------------- | --------------------------------------------------------- |
 * | Panel            | `training-panel`                                          |
 * | Terminals        | `terminal-{nodeId}` · srcP maxOccupants 5                 |
 * | Jumpers          | `jumper-N` · ends `jumper-N-end-a/b` · `jumper-N-cable`   |
 * | Rest posts       | `jumper-rest-N-a/b` on `jumper-rests`                     |
 * | Fixtures         | `ceiling-light-a/b`, `wall-light` (emissive mats exported)|
 * | Sign             | `workshop-sign`                                           |
 * | Fan              | `fan` · blades `fan-blades`                               |
 * | Switch           | `switch-light-b` · paddle `switch-light-b-toggle`         |
 * | Outlet           | `outlet-a` · `outlet-a-indicator`                         |
 * | Progress         | `progress-indicator`                                      |
 * | Intro / reset    | `intro-button`, `intro-panel` (hidden), `reset`           |
 * |                  | (`reset` mesh label reads NEW FAULT)                      |
 * | Hint             | `hint-button` (HINT — conceptual copy only)               |
 * | Meter            | `meter` · display `meter-display` · probe `meter-probe`   |
 * | Meter dock       | `meter-rest` on the training-panel apron (left)           |
 * | Test points      | `test-{nodeId}` on the panel face; switch `test-switch-   |
 * |                  | line` / `test-switch-load` beside LIGHT B                 |
 *
 * Default mesh seating: Light A jumper seated srcP ↔ outLightA; other leads
 * parked OPEN. Healthy room = all five outputs seated — hands/foundation
 * reseat from fault connections; this kit does not auto-seat the rest.
 */

export const ELECTRICAL_ROOM_STATION_ID = "electrical-room";

export const ELECTRICAL_ROOM_NODE_IDS = [
  ROOM_SOURCE_BUS_ID,
  ...ROOM_OUTPUT_IDS,
] as const;

export type ElectricalRoomNodeId = RoomPanelNodeId;
export type ElectricalRoomOutputId = RoomOutputId;

export const ELECTRICAL_ROOM_OUTPUT_IDS = ROOM_OUTPUT_IDS;

export interface ElectricalRoomJumper {
  id: string;
  group: THREE.Group;
  endA: THREE.Group;
  endB: THREE.Group;
  cable: THREE.Object3D;
}

export interface CircuitIndicator {
  mesh: THREE.Mesh;
  material: THREE.MeshStandardMaterial;
}

export interface ElectricalRoomKit {
  panel: THREE.Group;
  terminals: Record<ElectricalRoomNodeId, THREE.Group>;
  jumpers: ElectricalRoomJumper[];
  restPosts: THREE.Group;
  ceilingLightA: THREE.Mesh;
  ceilingLightAMaterial: THREE.MeshStandardMaterial;
  ceilingLightB: THREE.Mesh;
  ceilingLightBMaterial: THREE.MeshStandardMaterial;
  wallLight: THREE.Mesh;
  wallLightMaterial: THREE.MeshStandardMaterial;
  sign: THREE.Mesh;
  signMaterial: THREE.MeshStandardMaterial;
  fan: THREE.Group;
  fanBlades: THREE.Group;
  lightBSwitch: THREE.Group;
  outletA: THREE.Group;
  introButton: THREE.Group;
  introPanel: THREE.Group;
  reset: THREE.Group;
  hintButton: THREE.Group;
  meter: THREE.Group;
  meterProbe: THREE.Group;
  meterDisplay: THREE.Mesh;
  meterDisplayTexture: THREE.CanvasTexture;
  meterRest: THREE.Group;
  testPoints: Record<ElectricalRoomNodeId, THREE.Group>;
  progressIndicator: THREE.Mesh;
  indicators: Record<ElectricalRoomOutputId, CircuitIndicator>;
}

function createMats() {
  return {
    steel: new THREE.MeshStandardMaterial({
      color: 0x6a7280,
      roughness: 0.45,
      metalness: 0.55,
    }),
    panel: new THREE.MeshStandardMaterial({
      color: 0x2c352e,
      roughness: 0.72,
      metalness: 0.12,
    }),
    panelFace: new THREE.MeshStandardMaterial({
      color: 0x243028,
      roughness: 0.78,
      metalness: 0.08,
    }),
    apron: new THREE.MeshStandardMaterial({
      color: 0x3a4038,
      roughness: 0.65,
      metalness: 0.18,
    }),
    copper: new THREE.MeshStandardMaterial({
      color: 0xb87333,
      roughness: 0.4,
      metalness: 0.75,
    }),
    insulatorRed: new THREE.MeshStandardMaterial({
      color: 0x8b2e2e,
      roughness: 0.65,
      metalness: 0.1,
    }),
    insulatorBlack: new THREE.MeshStandardMaterial({
      color: 0x1e2228,
      roughness: 0.75,
      metalness: 0.08,
    }),
    rest: new THREE.MeshStandardMaterial({
      color: 0x4a5560,
      roughness: 0.55,
      metalness: 0.35,
    }),
    accent: new THREE.MeshStandardMaterial({
      color: 0x4a7a6a,
      roughness: 0.6,
      metalness: 0.1,
    }),
    rubber: new THREE.MeshStandardMaterial({
      color: 0x1c1e20,
      roughness: 0.92,
      metalness: 0.02,
    }),
    housing: new THREE.MeshStandardMaterial({
      color: 0x4a4e52,
      roughness: 0.55,
      metalness: 0.35,
    }),
    cover: new THREE.MeshStandardMaterial({
      color: 0xd8dbe0,
      roughness: 0.5,
      metalness: 0.05,
    }),
    slot: new THREE.MeshStandardMaterial({
      color: 0x1a1d24,
      roughness: 0.9,
      metalness: 0.0,
    }),
    fanBody: new THREE.MeshStandardMaterial({
      color: 0x5a6168,
      roughness: 0.5,
      metalness: 0.4,
    }),
    blade: new THREE.MeshStandardMaterial({
      color: 0xc8ccd0,
      roughness: 0.45,
      metalness: 0.15,
    }),
    tester: new THREE.MeshStandardMaterial({
      color: 0x2c322e,
      roughness: 0.58,
      metalness: 0.18,
    }),
    testerAccent: new THREE.MeshStandardMaterial({
      color: 0xc9a227,
      roughness: 0.48,
      metalness: 0.12,
    }),
  };
}

type Mats = ReturnType<typeof createMats>;

const OUTPUT_SPECS: ReadonlyArray<{
  nodeId: ElectricalRoomOutputId;
  label: string;
  x: number;
}> = [
  { nodeId: "outLightA", label: "LIGHT A", x: -0.28 },
  { nodeId: "outLightB", label: "LIGHT B", x: -0.02 },
  { nodeId: "outSign", label: "SIGN", x: 0.24 },
  { nodeId: "outFan", label: "FAN", x: 0.5 },
  { nodeId: "outOutlet", label: "OUTLET", x: 0.76 },
];

const SRC_P_X = -0.68;
const TERMINAL_Z = -0.02;
const TEST_JACK_Y = 0.148;
const TEST_JACK_Z = -0.163;
const JUMPER_COLORS = [0xb43c3c, 0x1e2228, 0xc4a024, 0x2f6f9a, 0x2f8f4e] as const;

interface TestJackGeos {
  bezel: THREE.CylinderGeometry;
  ring: THREE.CylinderGeometry;
  hole: THREE.CylinderGeometry;
}

function createTestJackGeos(): TestJackGeos {
  return {
    bezel: new THREE.CylinderGeometry(0.01, 0.011, 0.008, 10),
    ring: new THREE.CylinderGeometry(0.0068, 0.0068, 0.005, 8),
    hole: new THREE.CylinderGeometry(0.0034, 0.0034, 0.01, 8),
  };
}

/**
 * Build panel, jumpers, fixtures, sign, fan, switch, and outlet into `parent`.
 * Does not register grabs — mesh + userData hooks only.
 */
export function attachElectricalRoomFeatures(
  parent: THREE.Object3D,
): ElectricalRoomKit {
  const mats = createMats();
  const jackGeos = createTestJackGeos();

  const panelBuilt = buildTrainingPanel(mats, jackGeos);
  parent.add(panelBuilt.panel);

  const lights = buildCeilingFixtures(mats);
  parent.add(lights.group);

  const sign = buildWorkshopSign(mats);
  parent.add(sign.mesh);

  const fan = buildFan(mats);
  parent.add(fan.group);

  const lightBSwitch = buildLightBSwitch(mats, jackGeos);
  parent.add(lightBSwitch);

  const outletA = buildWallOutlet(mats);
  parent.add(outletA);

  return {
    panel: panelBuilt.panel,
    terminals: panelBuilt.terminals,
    jumpers: panelBuilt.jumpers,
    restPosts: panelBuilt.restPosts,
    ceilingLightA: lights.ceilingLightA,
    ceilingLightAMaterial: lights.ceilingLightAMaterial,
    ceilingLightB: lights.ceilingLightB,
    ceilingLightBMaterial: lights.ceilingLightBMaterial,
    wallLight: lights.wallLight,
    wallLightMaterial: lights.wallLightMaterial,
    sign: sign.mesh,
    signMaterial: sign.material,
    fan: fan.group,
    fanBlades: fan.blades,
    lightBSwitch,
    outletA,
    introButton: panelBuilt.introButton,
    introPanel: panelBuilt.introPanel,
    reset: panelBuilt.reset,
    hintButton: panelBuilt.hintButton,
    meter: panelBuilt.meter,
    meterProbe: panelBuilt.meterProbe,
    meterDisplay: panelBuilt.meterDisplay,
    meterDisplayTexture: panelBuilt.meterDisplayTexture,
    meterRest: panelBuilt.meterRest,
    testPoints: panelBuilt.testPoints,
    progressIndicator: panelBuilt.progressIndicator,
    indicators: panelBuilt.indicators,
  };
}

function buildTrainingPanel(
  mats: Mats,
  jackGeos: TestJackGeos,
): {
  panel: THREE.Group;
  terminals: Record<ElectricalRoomNodeId, THREE.Group>;
  jumpers: ElectricalRoomJumper[];
  restPosts: THREE.Group;
  introButton: THREE.Group;
  introPanel: THREE.Group;
  reset: THREE.Group;
  hintButton: THREE.Group;
  meter: THREE.Group;
  meterProbe: THREE.Group;
  meterDisplay: THREE.Mesh;
  meterDisplayTexture: THREE.CanvasTexture;
  meterRest: THREE.Group;
  testPoints: Record<ElectricalRoomNodeId, THREE.Group>;
  progressIndicator: THREE.Mesh;
  indicators: Record<ElectricalRoomOutputId, CircuitIndicator>;
} {
  const panel = new THREE.Group();
  panel.name = "training-panel";
  panel.userData = {
    id: ELECTRICAL_ROOM_STATION_ID,
    stationId: ELECTRICAL_ROOM_STATION_ID,
    kind: "station",
    conceptual: true,
    energized: false,
    mockup: "low-voltage-training",
  };
  panel.position.set(PANEL.x, PANEL.y, PANEL.z);

  const cabinet = new THREE.Mesh(
    new THREE.BoxGeometry(1.92, 0.78, 0.08),
    mats.panel,
  );
  cabinet.position.set(0, 0.28, -0.22);
  cabinet.castShadow = true;
  cabinet.receiveShadow = true;
  cabinet.name = "training-panel-cabinet";
  panel.add(cabinet);

  const face = new THREE.Mesh(
    new THREE.BoxGeometry(1.78, 0.52, 0.02),
    mats.panelFace,
  );
  face.position.set(0, 0.36, -0.175);
  face.name = "training-panel-face";
  panel.add(face);

  const apron = new THREE.Mesh(
    new THREE.BoxGeometry(1.92, 0.05, 0.44),
    mats.apron,
  );
  apron.position.set(0, -0.01, -0.02);
  apron.castShadow = true;
  apron.receiveShadow = true;
  apron.name = "training-panel-apron";
  panel.add(apron);

  const lip = new THREE.Mesh(
    new THREE.BoxGeometry(1.92, 0.03, 0.03),
    mats.steel,
  );
  lip.position.set(0, 0.02, 0.2);
  lip.name = "training-panel-lip";
  panel.add(lip);

  const title = makeLabel("TRAINING CONTROL  ·  LOW-VOLTAGE MOCKUP", {
    width: 0.92,
    height: 0.055,
    fontSize: 22,
    fill: "#d8e6df",
    bg: "rgba(18,28,26,0.88)",
  });
  title.position.set(-0.18, 0.58, -0.16);
  panel.add(title);

  const sourceBrick = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.09, 0.14),
    mats.steel,
  );
  sourceBrick.position.set(SRC_P_X, 0.06, 0.08);
  sourceBrick.castShadow = true;
  sourceBrick.name = "panel-source-brick";
  panel.add(sourceBrick);

  const srcTag = makeLabel("12V TRAINING", {
    width: 0.2,
    height: 0.032,
    fontSize: 18,
    fill: "#e8f0ea",
    bg: "rgba(20,32,28,0.88)",
  });
  srcTag.position.set(SRC_P_X, 0.09, 0.155);
  panel.add(srcTag);

  const terminals = {} as Record<ElectricalRoomNodeId, THREE.Group>;

  const srcP = buildTerminal(mats, {
    nodeId: ROOM_SOURCE_BUS_ID,
    label: "SOURCE  +",
    polarity: "P",
    large: true,
    maxOccupants: ROOM_SOURCE_BUS_MAX_OCCUPANTS,
  });
  srcP.position.set(SRC_P_X, 0.025, TERMINAL_Z);
  panel.add(srcP);
  terminals.srcP = srcP;

  for (const spec of OUTPUT_SPECS) {
    const terminal = buildTerminal(mats, {
      nodeId: spec.nodeId,
      label: spec.label,
      polarity: "P",
      large: false,
      maxOccupants: 1,
    });
    terminal.position.set(spec.x, 0.025, TERMINAL_Z);
    panel.add(terminal);
    terminals[spec.nodeId] = terminal;
  }

  const testPoints = {} as Record<ElectricalRoomNodeId, THREE.Group>;
  const srcJack = buildTestJack(mats, jackGeos, {
    name: "test-srcP",
    nodeId: ROOM_SOURCE_BUS_ID,
    source: true,
  });
  srcJack.position.set(SRC_P_X, TEST_JACK_Y, TEST_JACK_Z);
  panel.add(srcJack);
  testPoints.srcP = srcJack;

  for (const spec of OUTPUT_SPECS) {
    const jack = buildTestJack(mats, jackGeos, {
      name: `test-${spec.nodeId}`,
      nodeId: spec.nodeId,
    });
    jack.position.set(spec.x, TEST_JACK_Y, TEST_JACK_Z);
    panel.add(jack);
    testPoints[spec.nodeId] = jack;
  }

  const indicators = {} as Record<ElectricalRoomOutputId, CircuitIndicator>;
  for (const spec of OUTPUT_SPECS) {
    const indicator = buildCircuitIndicator(spec.nodeId, spec.nodeId === "outLightA");
    indicator.mesh.position.set(spec.x, 0.28, -0.16);
    panel.add(indicator.mesh);
    indicators[spec.nodeId] = indicator;
  }

  const progressIndicator = buildProgressIndicator(mats);
  progressIndicator.position.set(0.42, 0.58, -0.16);
  panel.add(progressIndicator);

  const rests = buildJumperRests(mats);
  panel.add(rests.group);

  const jumpers = buildJumpers(mats, rests.slots);
  for (const jumper of jumpers) {
    panel.add(jumper.group);
  }

  const intro = buildIntroAffordances(mats);
  intro.button.position.set(-0.78, 0.22, -0.16);
  intro.panel.position.set(-0.52, 0.42, 0.12);
  panel.add(intro.button, intro.panel);

  const hintButton = buildHintButton(mats);
  hintButton.position.set(-0.78, 0.155, -0.16);
  panel.add(hintButton);

  // Object name stays `reset` so hands still find env.reset. Visible label: NEW FAULT.
  const reset = buildResetControl(mats);
  reset.position.set(0.82, 0.05, 0.14);
  panel.add(reset);

  const meterRest = buildMeterRest(mats);
  meterRest.position.set(-0.88, 0.018, 0.11);
  panel.add(meterRest);

  const meterBuilt = buildMeter(mats);
  // Sit in the apron cradle, display tilted toward standing eye height.
  meterBuilt.meter.position.set(-0.88, 0.078, 0.118);
  meterBuilt.meter.rotation.x = -0.34;
  panel.add(meterBuilt.meter);

  const mat = new THREE.Mesh(
    new THREE.CircleGeometry(0.42, 20),
    mats.rubber,
  );
  mat.rotation.x = -Math.PI / 2;
  mat.position.set(0, -PANEL.y + 0.004, 0.95);
  mat.receiveShadow = true;
  mat.name = "panel-stand-mat";
  panel.add(mat);

  return {
    panel,
    terminals,
    jumpers,
    restPosts: rests.group,
    introButton: intro.button,
    introPanel: intro.panel,
    reset,
    hintButton,
    meter: meterBuilt.meter,
    meterProbe: meterBuilt.meterProbe,
    meterDisplay: meterBuilt.meterDisplay,
    meterDisplayTexture: meterBuilt.meterDisplayTexture,
    meterRest,
    testPoints,
    progressIndicator,
    indicators,
  };
}

function buildTerminal(
  mats: Mats,
  spec: {
    nodeId: ElectricalRoomNodeId;
    label: string;
    polarity: "P" | "N";
    large: boolean;
    maxOccupants: number;
  },
): THREE.Group {
  const g = new THREE.Group();
  g.name = `terminal-${spec.nodeId}`;
  g.userData = {
    kind: "terminal",
    nodeId: spec.nodeId,
    stationId: ELECTRICAL_ROOM_STATION_ID,
    placementTarget: true,
    accepts: "jumper-end",
    polarity: spec.polarity,
    maxOccupants: spec.maxOccupants,
  };

  const padW = spec.large ? 0.16 : 0.11;
  const postR = spec.large ? 0.026 : 0.02;
  const capR = spec.large ? 0.032 : 0.026;
  const capMat = spec.polarity === "P" ? mats.insulatorRed : mats.insulatorBlack;

  const pad = new THREE.Mesh(
    new THREE.BoxGeometry(padW, 0.014, padW),
    mats.steel,
  );
  pad.position.y = 0.007;
  pad.receiveShadow = true;
  pad.name = `terminal-${spec.nodeId}-pad`;
  g.add(pad);

  const post = new THREE.Mesh(
    new THREE.CylinderGeometry(postR, postR + 0.002, 0.05, 12),
    mats.copper,
  );
  post.position.y = 0.038;
  post.castShadow = true;
  post.name = `terminal-${spec.nodeId}-post`;
  g.add(post);

  const cap = new THREE.Mesh(
    new THREE.CylinderGeometry(capR, capR - 0.002, 0.02, 12),
    capMat,
  );
  cap.position.y = 0.07;
  cap.castShadow = true;
  cap.name = `terminal-${spec.nodeId}-cap`;
  g.add(cap);

  if (spec.large) {
    const bus = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.012, 0.06),
      mats.copper,
    );
    bus.position.set(0, 0.018, 0.0);
    bus.name = "terminal-srcP-bus";
    g.add(bus);
  }

  const label = makeLabel(spec.label, {
    width: spec.large ? 0.16 : 0.12,
    height: 0.036,
    fontSize: spec.large ? 22 : 20,
    fill: spec.polarity === "P" ? "#f4d4d0" : "#d8dde4",
    bg: "rgba(16,18,22,0.86)",
  });
  label.position.set(0, 0.02, padW * 0.52 + 0.012);
  label.rotation.x = -0.42;
  g.add(label);

  return g;
}

function buildCircuitIndicator(
  nodeId: ElectricalRoomOutputId,
  primed: boolean,
): CircuitIndicator {
  const material = new THREE.MeshStandardMaterial({
    color: primed ? 0xc8e8a8 : 0x3a4438,
    roughness: 0.35,
    metalness: 0.05,
    emissive: primed ? 0x88cc55 : 0x223318,
    emissiveIntensity: primed ? 0.45 : 0.06,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(0.022, 10, 8), material);
  mesh.name = `indicator-${nodeId}`;
  mesh.userData = {
    kind: "circuit-indicator",
    nodeId,
    stationId: ELECTRICAL_ROOM_STATION_ID,
    emissiveMaterial: true,
  };
  const bezel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.028, 0.03, 0.012, 12),
    new THREE.MeshStandardMaterial({
      color: 0x2a2e32,
      roughness: 0.5,
      metalness: 0.4,
    }),
  );
  bezel.rotation.x = Math.PI / 2;
  bezel.position.z = -0.008;
  bezel.name = `indicator-${nodeId}-bezel`;
  mesh.add(bezel);
  return { mesh, material };
}

function buildProgressIndicator(mats: Mats): THREE.Mesh {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "rgba(18, 28, 26, 0.92)";
  roundRect(ctx, 8, 10, canvas.width - 16, canvas.height - 20, 12);
  ctx.fill();
  ctx.fillStyle = "#d8e6df";
  ctx.font = "700 44px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("ROOM POWER 1/5", canvas.width / 2, canvas.height / 2 + 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.42, 0.09),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    }),
  );
  mesh.name = "progress-indicator";
  mesh.userData = {
    kind: "progress-indicator",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    poweredCount: 1,
    total: 5,
  };

  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(0.44, 0.1, 0.016),
    mats.panel,
  );
  plate.position.z = -0.01;
  plate.name = "progress-indicator-plate";
  mesh.add(plate);

  return mesh;
}

interface RestSlot {
  jumperId: string;
  end: "a" | "b";
  x: number;
  z: number;
}

function buildJumperRests(mats: Mats): {
  group: THREE.Group;
  slots: RestSlot[];
} {
  const g = new THREE.Group();
  g.name = "jumper-rests";
  g.userData = { kind: "jumper-rests", stationId: ELECTRICAL_ROOM_STATION_ID };

  const rail = new THREE.Mesh(
    new THREE.BoxGeometry(1.52, 0.016, 0.08),
    mats.insulatorBlack,
  );
  rail.position.set(0.04, 0.012, 0.18);
  rail.name = "jumper-rest-rail";
  g.add(rail);

  const slots: RestSlot[] = [];
  const pairCenters = [-0.64, -0.32, 0.0, 0.32, 0.64];
  const pairSpan = 0.055;

  for (let i = 0; i < 5; i++) {
    const jumperId = `jumper-${i}`;
    for (const end of ["a", "b"] as const) {
      const x = pairCenters[i] + (end === "a" ? pairSpan : -pairSpan);
      const z = 0.18;
      slots.push({ jumperId, end, x, z });

      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.012, 0.014, 0.028, 8),
        mats.rest,
      );
      post.position.set(x, 0.03, z);
      post.name = `jumper-rest-${i}-${end}`;
      post.userData = {
        kind: "jumper-rest",
        jumperId,
        end,
        stationId: ELECTRICAL_ROOM_STATION_ID,
        placementTarget: true,
        accepts: "jumper-end",
      };
      g.add(post);
    }
  }

  return { group: g, slots };
}

function buildJumpers(
  mats: Mats,
  slots: RestSlot[],
): ElectricalRoomJumper[] {
  const jumpers: ElectricalRoomJumper[] = [];

  for (let i = 0; i < 5; i++) {
    const jumperId = `jumper-${i}`;
    const color = JUMPER_COLORS[i];
    const sleeveMat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.55,
      metalness: 0.08,
    });

    const group = new THREE.Group();
    group.name = jumperId;
    group.userData = {
      kind: "jumper",
      jumperId,
      stationId: ELECTRICAL_ROOM_STATION_ID,
    };

    const slotA = slots.find((s) => s.jumperId === jumperId && s.end === "a")!;
    const slotB = slots.find((s) => s.jumperId === jumperId && s.end === "b")!;

    // Default mesh seating is Light A only. Healthy = all five outputs seated;
    // hands/foundation reseat from fault connections.
    const seated = i === 0;
    const posA = seated
      ? new THREE.Vector3(SRC_P_X, 0.088, TERMINAL_Z)
      : new THREE.Vector3(slotA.x, 0.058, slotA.z);
    const posB = seated
      ? new THREE.Vector3(OUTPUT_SPECS[0].x, 0.088, TERMINAL_Z)
      : new THREE.Vector3(slotB.x, 0.058, slotB.z);

    const endA = buildJumperEnd(mats, sleeveMat, jumperId, "a");
    endA.position.copy(posA);
    if (seated) {
      endA.userData.defaultTerminal = "srcP";
    }
    group.add(endA);

    const endB = buildJumperEnd(mats, sleeveMat, jumperId, "b");
    endB.position.copy(posB);
    if (seated) {
      endB.userData.defaultTerminal = "outLightA";
    }
    group.add(endB);

    const from = posA.clone();
    from.y += 0.012;
    const to = posB.clone();
    to.y += 0.012;
    const cable = buildJumperCable(jumperId, from, to, color);
    group.add(cable);

    jumpers.push({ id: jumperId, group, endA, endB, cable });
  }

  return jumpers;
}

function buildJumperEnd(
  mats: Mats,
  sleeveMat: THREE.MeshStandardMaterial,
  jumperId: string,
  end: "a" | "b",
): THREE.Group {
  const g = new THREE.Group();
  g.name = `${jumperId}-end-${end}`;
  g.userData = {
    kind: "jumper-end",
    jumperId,
    end,
    grabable: true,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };

  const tip = new THREE.Mesh(
    new THREE.CylinderGeometry(0.008, 0.007, 0.028, 8),
    mats.copper,
  );
  tip.position.y = -0.02;
  tip.name = `${jumperId}-end-${end}-tip`;
  g.add(tip);

  const sleeve = new THREE.Mesh(
    new THREE.CylinderGeometry(0.016, 0.015, 0.05, 10),
    sleeveMat,
  );
  sleeve.position.y = 0.018;
  sleeve.castShadow = true;
  sleeve.name = `${jumperId}-end-${end}-sleeve`;
  g.add(sleeve);

  const collar = new THREE.Mesh(
    new THREE.CylinderGeometry(0.018, 0.016, 0.012, 10),
    mats.steel,
  );
  collar.position.y = -0.004;
  collar.name = `${jumperId}-end-${end}-collar`;
  g.add(collar);

  return g;
}

function buildJumperCable(
  jumperId: string,
  from: THREE.Vector3,
  to: THREE.Vector3,
  color: number,
): THREE.Mesh {
  const mid = from.clone().add(to).multiplyScalar(0.5);
  mid.y += 0.05;
  const curve = new THREE.CatmullRomCurve3([from, mid, to]);
  const geo = new THREE.TubeGeometry(curve, 6, 0.006, 5, false);
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.55,
    metalness: 0.08,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = `${jumperId}-cable`;
  mesh.userData = {
    kind: "jumper-cable",
    jumperId,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };
  return mesh;
}

function buildIntroAffordances(mats: Mats): {
  button: THREE.Group;
  panel: THREE.Group;
} {
  const button = new THREE.Group();
  button.name = "intro-button";
  button.userData = {
    kind: "intro-button",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    grabable: false,
    selectTarget: true,
  };

  const pad = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.07, 0.03),
    mats.accent,
  );
  pad.castShadow = true;
  pad.name = "intro-button-mesh";
  button.add(pad);

  const btnLabel = makeLabel("WHAT IS THIS?", {
    width: 0.2,
    height: 0.04,
    fontSize: 18,
    fill: "#eef6f2",
    bg: "rgba(20,40,34,0.88)",
  });
  btnLabel.position.set(0, 0, 0.018);
  button.add(btnLabel);

  const panel = new THREE.Group();
  panel.name = "intro-panel";
  panel.userData = {
    kind: "intro-panel",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    defaultVisible: false,
  };
  panel.visible = false;

  const card = new THREE.Mesh(
    new THREE.BoxGeometry(0.52, 0.32, 0.02),
    mats.panel,
  );
  card.name = "intro-panel-card";
  panel.add(card);

  const copy = makeLabel(
    "Something in the workshop\nisn't working.\nFind the fault.",
    {
      width: 0.48,
      height: 0.28,
      fontSize: 24,
      fill: "#dce8e2",
      bg: "rgba(24,32,36,0.92)",
      multiline: true,
    },
  );
  copy.position.z = 0.012;
  panel.add(copy);

  return { button, panel };
}

function buildResetControl(mats: Mats): THREE.Group {
  const g = new THREE.Group();
  g.name = "reset";
  g.userData = {
    kind: "reset",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    selectTarget: true,
    scope: "room",
  };

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.05, 0.1),
    new THREE.MeshStandardMaterial({
      color: 0x3a3424,
      roughness: 0.55,
      metalness: 0.15,
    }),
  );
  body.castShadow = true;
  body.name = "reset-mesh";
  g.add(body);

  const label = makeLabel("NEW FAULT", {
    width: 0.19,
    height: 0.036,
    fontSize: 20,
    fill: "#f0e6d0",
    bg: "rgba(40,32,18,0.9)",
  });
  label.position.set(0, 0.028, 0.052);
  g.add(label);

  const rim = new THREE.Mesh(
    new THREE.BoxGeometry(0.23, 0.01, 0.11),
    mats.steel,
  );
  rim.position.y = -0.02;
  rim.name = "reset-rim";
  g.add(rim);

  return g;
}

function buildHintButton(mats: Mats): THREE.Group {
  const g = new THREE.Group();
  g.name = "hint-button";
  g.visible = false;
  g.userData = {
    kind: "hint-button",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    selectTarget: true,
    defaultVisible: false,
  };

  const pad = new THREE.Mesh(
    new THREE.BoxGeometry(0.1, 0.036, 0.02),
    mats.housing,
  );
  pad.name = "hint-button-mesh";
  g.add(pad);

  const label = makeLabel("HINT", {
    width: 0.088,
    height: 0.028,
    fontSize: 18,
    fill: "#c8d0cc",
    bg: "rgba(24,28,30,0.88)",
  });
  label.position.set(0, 0, 0.012);
  g.add(label);

  return g;
}

function buildTestJack(
  mats: Mats,
  geos: TestJackGeos,
  spec: {
    name: string;
    nodeId: string;
    source?: boolean;
    scale?: number;
    extra?: Record<string, unknown>;
  },
): THREE.Group {
  const g = new THREE.Group();
  g.name = spec.name;
  g.userData = {
    kind: "test-point",
    nodeId: spec.nodeId,
    stationId: ELECTRICAL_ROOM_STATION_ID,
    ...spec.extra,
  };
  const s = spec.scale ?? 1;
  g.scale.setScalar(s);

  const bezel = new THREE.Mesh(
    geos.bezel,
    spec.source ? mats.insulatorRed : mats.steel,
  );
  bezel.rotation.x = Math.PI / 2;
  bezel.name = `${spec.name}-bezel`;
  g.add(bezel);

  const ring = new THREE.Mesh(geos.ring, mats.copper);
  ring.rotation.x = Math.PI / 2;
  ring.position.z = 0.002;
  ring.name = `${spec.name}-ring`;
  g.add(ring);

  const hole = new THREE.Mesh(geos.hole, mats.slot);
  hole.rotation.x = Math.PI / 2;
  hole.position.z = 0.001;
  hole.name = `${spec.name}-hole`;
  g.add(hole);

  return g;
}

function buildMeterRest(mats: Mats): THREE.Group {
  const g = new THREE.Group();
  g.name = "meter-rest";
  g.userData = {
    kind: "meter-rest",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    placementTarget: true,
    accepts: "meter",
  };

  const tray = new THREE.Mesh(
    new THREE.BoxGeometry(0.078, 0.008, 0.055),
    mats.steel,
  );
  tray.position.y = 0.004;
  tray.receiveShadow = true;
  tray.name = "meter-rest-tray";
  g.add(tray);

  const back = new THREE.Mesh(
    new THREE.BoxGeometry(0.078, 0.026, 0.008),
    mats.steel,
  );
  back.position.set(0, 0.017, -0.024);
  back.name = "meter-rest-back";
  g.add(back);

  const hook = new THREE.Mesh(
    new THREE.CylinderGeometry(0.005, 0.005, 0.028, 8),
    mats.steel,
  );
  hook.rotation.x = Math.PI / 2;
  hook.position.set(0, 0.02, -0.008);
  hook.name = "meter-rest-hook";
  g.add(hook);

  const lipL = new THREE.Mesh(
    new THREE.BoxGeometry(0.007, 0.014, 0.048),
    mats.steel,
  );
  lipL.position.set(-0.036, 0.011, 0.002);
  lipL.name = "meter-rest-lip-l";
  g.add(lipL);

  const lipR = lipL.clone();
  lipR.position.x = 0.036;
  lipR.name = "meter-rest-lip-r";
  g.add(lipR);

  return g;
}

function buildMeterDisplay(): {
  mesh: THREE.Mesh;
  texture: THREE.CanvasTexture;
} {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#07140c";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#6fdf88";
  ctx.font = "700 64px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("----", canvas.width / 2, canvas.height / 2 + 4);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.044, 0.022),
    new THREE.MeshBasicMaterial({
      map: texture,
      toneMapped: false,
    }),
  );
  mesh.name = "meter-display";
  mesh.userData = {
    kind: "meter-display",
    canvas,
    texture,
    placeholder: "----",
  };

  return { mesh, texture };
}

function buildMeter(mats: Mats): {
  meter: THREE.Group;
  meterProbe: THREE.Group;
  meterDisplay: THREE.Mesh;
  meterDisplayTexture: THREE.CanvasTexture;
} {
  const meter = new THREE.Group();
  meter.name = "meter";
  meter.userData = {
    kind: "meter",
    grabable: true,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.056, 0.112, 0.026),
    mats.tester,
  );
  body.castShadow = true;
  body.name = "meter-body";
  meter.add(body);

  const accent = new THREE.Mesh(
    new THREE.BoxGeometry(0.058, 0.016, 0.028),
    mats.testerAccent,
  );
  accent.position.y = -0.05;
  accent.name = "meter-accent";
  meter.add(accent);

  const grip = new THREE.Mesh(
    new THREE.BoxGeometry(0.052, 0.038, 0.024),
    mats.rubber,
  );
  grip.position.y = -0.026;
  grip.name = "meter-grip";
  meter.add(grip);

  const displayBuilt = buildMeterDisplay();
  displayBuilt.mesh.position.set(0, 0.03, 0.0145);
  meter.add(displayBuilt.mesh);

  const bezel = new THREE.Mesh(
    new THREE.BoxGeometry(0.048, 0.028, 0.004),
    mats.insulatorBlack,
  );
  bezel.position.set(0, 0.03, 0.012);
  bezel.name = "meter-display-bezel";
  meter.add(bezel);

  // Black COM jack is visual only — return path is conceptually at source.
  const com = new THREE.Mesh(
    new THREE.CylinderGeometry(0.0055, 0.0055, 0.007, 8),
    mats.insulatorBlack,
  );
  com.rotation.x = Math.PI / 2;
  com.position.set(-0.012, -0.004, 0.014);
  com.name = "meter-com";
  meter.add(com);

  const vJack = new THREE.Mesh(
    new THREE.CylinderGeometry(0.0055, 0.0055, 0.007, 8),
    mats.insulatorRed,
  );
  vJack.rotation.x = Math.PI / 2;
  vJack.position.set(0.012, -0.004, 0.014);
  vJack.name = "meter-v-jack";
  meter.add(vJack);

  const probe = new THREE.Group();
  probe.name = "meter-probe";
  probe.userData = {
    kind: "probe",
    polarity: "P",
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };
  probe.position.set(0, 0.068, 0);

  const sleeve = new THREE.Mesh(
    new THREE.CylinderGeometry(0.0065, 0.0075, 0.05, 8),
    mats.insulatorRed,
  );
  sleeve.position.y = 0.026;
  sleeve.name = "meter-probe-sleeve";
  probe.add(sleeve);

  const tip = new THREE.Mesh(
    new THREE.CylinderGeometry(0.0026, 0.0042, 0.02, 8),
    mats.copper,
  );
  tip.position.y = 0.058;
  tip.name = "meter-probe-tip";
  probe.add(tip);

  meter.add(probe);

  return {
    meter,
    meterProbe: probe,
    meterDisplay: displayBuilt.mesh,
    meterDisplayTexture: displayBuilt.texture,
  };
}

function buildCeilingFixtures(mats: Mats): {
  group: THREE.Group;
  ceilingLightA: THREE.Mesh;
  ceilingLightAMaterial: THREE.MeshStandardMaterial;
  ceilingLightB: THREE.Mesh;
  ceilingLightBMaterial: THREE.MeshStandardMaterial;
  wallLight: THREE.Mesh;
  wallLightMaterial: THREE.MeshStandardMaterial;
} {
  const group = new THREE.Group();
  group.name = "room-fixtures";

  const aMat = new THREE.MeshStandardMaterial({
    color: 0xe8dcb0,
    roughness: 0.45,
    metalness: 0.0,
    emissive: 0xf0d890,
    emissiveIntensity: 0.28,
  });
  const ceilingLightA = buildShopLight(
    "ceiling-light-a",
    aMat,
    mats.housing,
    true,
  );
  ceilingLightA.position.set(
    CEILING_LIGHT_A.x,
    CEILING_LIGHT_A.y,
    CEILING_LIGHT_A.z,
  );
  group.add(ceilingLightA);

  const bMat = new THREE.MeshStandardMaterial({
    color: 0x5a5e62,
    roughness: 0.55,
    metalness: 0.05,
    emissive: 0x334044,
    emissiveIntensity: 0.04,
  });
  const ceilingLightB = buildShopLight(
    "ceiling-light-b",
    bMat,
    mats.housing,
    false,
  );
  ceilingLightB.position.set(
    CEILING_LIGHT_B.x,
    CEILING_LIGHT_B.y,
    CEILING_LIGHT_B.z,
  );
  group.add(ceilingLightB);

  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x4a4e52,
    roughness: 0.5,
    metalness: 0.08,
    emissive: 0x2a2418,
    emissiveIntensity: 0.03,
  });
  const wallLight = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.12, 0.1),
    wallMat,
  );
  wallLight.name = "wall-light";
  wallLight.position.set(WALL_LIGHT.x, WALL_LIGHT.y, WALL_LIGHT.z);
  wallLight.userData = {
    kind: "fixture",
    fixtureId: "wall-light",
    emissiveMaterial: true,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };
  const wallHood = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.04, 0.12),
    mats.housing,
  );
  wallHood.position.y = 0.08;
  wallHood.name = "wall-light-hood";
  wallLight.add(wallHood);
  group.add(wallLight);

  return {
    group,
    ceilingLightA,
    ceilingLightAMaterial: aMat,
    ceilingLightB,
    ceilingLightBMaterial: bMat,
    wallLight,
    wallLightMaterial: wallMat,
  };
}

function buildShopLight(
  name: string,
  diffuserMat: THREE.MeshStandardMaterial,
  housingMat: THREE.MeshStandardMaterial,
  primed: boolean,
): THREE.Mesh {
  const diffuser = new THREE.Mesh(
    new THREE.BoxGeometry(1.05, 0.03, 0.16),
    diffuserMat,
  );
  diffuser.name = name;
  diffuser.userData = {
    kind: "fixture",
    fixtureId: name,
    deviceId: name === "ceiling-light-a" ? "ceilingLightA" : "ceilingLightB",
    emissiveMaterial: true,
    primed,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };

  const housing = new THREE.Mesh(
    new THREE.BoxGeometry(1.12, 0.05, 0.2),
    housingMat,
  );
  housing.position.y = 0.04;
  housing.name = `${name}-housing`;
  diffuser.add(housing);

  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.012, 0.012, 0.12, 6),
    housingMat,
  );
  stem.position.y = 0.1;
  stem.name = `${name}-stem`;
  diffuser.add(stem);

  return diffuser;
}

function buildWorkshopSign(_mats: Mats): {
  mesh: THREE.Mesh;
  material: THREE.MeshStandardMaterial;
} {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#12151a";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#3a4048";
  ctx.font = "700 92px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("ELECTRICAL LAB", canvas.width / 2, 100);
  ctx.font = "600 48px system-ui, sans-serif";
  ctx.fillText("WORKSHOP", canvas.width / 2, 178);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  const material = new THREE.MeshStandardMaterial({
    map: texture,
    emissiveMap: texture,
    color: 0x2a2e32,
    emissive: 0x88aacc,
    emissiveIntensity: 0.04,
    roughness: 0.55,
    metalness: 0.08,
  });

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 0.42), material);
  mesh.name = "workshop-sign";
  mesh.position.set(SIGN.x, SIGN.y, SIGN.z);
  mesh.userData = {
    kind: "sign",
    deviceId: "sign",
    emissiveMaterial: true,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };

  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(2.68, 0.48, 0.03),
    new THREE.MeshStandardMaterial({
      color: 0x2a2e32,
      roughness: 0.6,
      metalness: 0.25,
    }),
  );
  frame.position.z = -0.02;
  frame.name = "workshop-sign-frame";
  mesh.add(frame);

  return { mesh, material };
}

function buildFan(mats: Mats): { group: THREE.Group; blades: THREE.Group } {
  const group = new THREE.Group();
  group.name = "fan";
  group.position.set(FAN.x, FAN.y, FAN.z);
  group.userData = {
    kind: "fan",
    deviceId: "fan",
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };

  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14, 0.16, 0.04, 10),
    mats.fanBody,
  );
  base.position.y = 0.02;
  base.castShadow = true;
  base.name = "fan-base";
  group.add(base);

  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.018, 0.022, 1.05, 8),
    mats.fanBody,
  );
  pole.position.y = 0.54;
  pole.castShadow = true;
  pole.name = "fan-pole";
  group.add(pole);

  const motor = new THREE.Mesh(
    new THREE.CylinderGeometry(0.055, 0.06, 0.1, 10),
    mats.steel,
  );
  motor.rotation.x = Math.PI / 2;
  motor.position.set(0, 1.12, 0.02);
  motor.castShadow = true;
  motor.name = "fan-motor";
  group.add(motor);

  // Face the work area (origin), not the corner wall.
  group.rotation.y = Math.atan2(-FAN.x, -FAN.z);

  const cage = new THREE.Mesh(
    new THREE.TorusGeometry(0.22, 0.01, 6, 16),
    mats.steel,
  );
  cage.position.set(0, 1.12, 0.08);
  cage.name = "fan-cage";
  group.add(cage);

  const blades = new THREE.Group();
  blades.name = "fan-blades";
  blades.position.set(0, 1.12, 0.07);
  blades.userData = { kind: "fan-blades", rotateAxis: "z" };

  for (let i = 0; i < 3; i++) {
    const blade = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 0.045, 0.008),
      mats.blade,
    );
    const ang = (i / 3) * Math.PI * 2;
    blade.position.set(Math.cos(ang) * 0.1, Math.sin(ang) * 0.1, 0);
    blade.rotation.z = ang;
    blade.name = `fan-blade-${i}`;
    blades.add(blade);
  }

  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(0.028, 0.028, 0.02, 8),
    mats.steel,
  );
  hub.rotation.x = Math.PI / 2;
  hub.name = "fan-hub";
  blades.add(hub);
  group.add(blades);

  return { group, blades };
}

function buildLightBSwitch(mats: Mats, jackGeos: TestJackGeos): THREE.Group {
  const g = new THREE.Group();
  g.name = "switch-light-b";
  g.position.set(SWITCH.x, SWITCH.y, SWITCH.z);
  g.rotation.y = Math.PI / 2;
  g.userData = {
    kind: "switch",
    switchId: "switchLightB",
    circuit: "outLightB",
    state: "off",
    stationId: ELECTRICAL_ROOM_STATION_ID,
    selectTarget: true,
  };

  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.13, 0.012),
    mats.cover,
  );
  plate.name = "switch-light-b-plate";
  g.add(plate);

  const toggle = new THREE.Mesh(
    new THREE.BoxGeometry(0.028, 0.055, 0.018),
    new THREE.MeshStandardMaterial({
      color: 0xe8e4dc,
      roughness: 0.45,
      metalness: 0.05,
    }),
  );
  toggle.name = "switch-light-b-toggle";
  toggle.position.set(0, -0.012, 0.014);
  toggle.rotation.x = 0.38;
  toggle.userData = { kind: "switch-toggle", state: "off" };
  g.add(toggle);

  const onTag = makeLabel("ON", {
    width: 0.05,
    height: 0.02,
    fontSize: 20,
    fill: "#1a1d24",
    bg: "rgba(236,238,244,0.0)",
  });
  onTag.position.set(0, 0.048, 0.008);
  g.add(onTag);

  const offTag = makeLabel("OFF", {
    width: 0.05,
    height: 0.02,
    fontSize: 18,
    fill: "#5a6068",
    bg: "rgba(236,238,244,0.0)",
  });
  offTag.position.set(0, -0.05, 0.008);
  g.add(offTag);

  const circuitTag = makeLabel("LIGHT B", {
    width: 0.12,
    height: 0.03,
    fontSize: 18,
    fill: "#d7dde8",
    bg: "rgba(20,24,32,0.78)",
  });
  circuitTag.position.set(0, 0.09, 0.008);
  g.add(circuitTag);

  // Line / load test jacks beside the plate — metal dots, not orbs.
  const jackPlate = new THREE.Mesh(
    new THREE.BoxGeometry(0.028, 0.11, 0.01),
    mats.cover,
  );
  jackPlate.position.set(0.056, 0, 0);
  jackPlate.name = "switch-light-b-test-plate";
  g.add(jackPlate);

  const testSwitchLine = buildTestJack(mats, jackGeos, {
    name: "test-switch-line",
    nodeId: "outLightB",
    scale: 0.82,
    extra: { switchRole: "line" },
  });
  testSwitchLine.position.set(0.056, 0.028, 0.008);
  g.add(testSwitchLine);

  const testSwitchLoad = buildTestJack(mats, jackGeos, {
    name: "test-switch-load",
    nodeId: SWITCH_LOAD_TEST_NODE_ID,
    scale: 0.82,
    extra: { switchRole: "load" },
  });
  testSwitchLoad.position.set(0.056, -0.022, 0.008);
  g.add(testSwitchLoad);

  return g;
}

function buildWallOutlet(mats: Mats): THREE.Group {
  const g = new THREE.Group();
  g.name = "outlet-a";
  g.position.set(OUTLET_A.x, OUTLET_A.y, OUTLET_A.z);
  g.rotation.y = Math.PI / 2;
  g.userData = {
    kind: "outlet",
    outletId: "outlet-a",
    deviceId: "outlet",
    circuit: "outOutlet",
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };

  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(0.075, 0.125, 0.01),
    mats.cover,
  );
  plate.name = "outlet-a-plate";
  g.add(plate);

  addDuplexFace(g, mats, 0.028);
  addDuplexFace(g, mats, -0.028);

  const indicatorMat = new THREE.MeshStandardMaterial({
    color: 0x3a4438,
    roughness: 0.4,
    metalness: 0.05,
    emissive: 0x335522,
    emissiveIntensity: 0.05,
  });
  const indicator = new THREE.Mesh(
    new THREE.SphereGeometry(0.008, 8, 6),
    indicatorMat,
  );
  indicator.name = "outlet-a-indicator";
  indicator.position.set(0.028, 0.048, 0.008);
  indicator.userData = {
    kind: "powered-indicator",
    emissiveMaterial: true,
    stationId: ELECTRICAL_ROOM_STATION_ID,
  };
  g.add(indicator);

  const tag = makeLabel("OUTLET", {
    width: 0.1,
    height: 0.028,
    fontSize: 18,
    fill: "#d7dde8",
    bg: "rgba(20,24,32,0.78)",
  });
  tag.position.set(0, 0.085, 0.008);
  g.add(tag);

  return g;
}

function addDuplexFace(parent: THREE.Group, mats: Mats, y: number): void {
  const z = 0.008;
  const neutral = new THREE.Mesh(
    new THREE.BoxGeometry(0.008, 0.02, 0.004),
    mats.slot,
  );
  neutral.position.set(-0.01, y, z);
  parent.add(neutral);

  const hot = new THREE.Mesh(
    new THREE.BoxGeometry(0.008, 0.014, 0.004),
    mats.slot,
  );
  hot.position.set(0.01, y, z);
  parent.add(hot);

  const gnd = new THREE.Mesh(
    new THREE.CylinderGeometry(0.0035, 0.0035, 0.004, 8),
    mats.slot,
  );
  gnd.rotation.x = Math.PI / 2;
  gnd.position.set(0, y - 0.016, z);
  parent.add(gnd);
}

function makeLabel(
  text: string,
  opts: {
    width: number;
    height: number;
    fontSize: number;
    fill: string;
    bg: string;
    multiline?: boolean;
  },
): THREE.Mesh {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = opts.multiline ? 256 : 64;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = opts.bg;
  const pad = 6;
  roundRect(ctx, pad, pad, canvas.width - pad * 2, canvas.height - pad * 2, 10);
  ctx.fill();
  ctx.fillStyle = opts.fill;
  ctx.font = `700 ${opts.fontSize}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (opts.multiline) {
    const lines = text.split("\n");
    const lineH = opts.fontSize + 10;
    const startY = canvas.height / 2 - ((lines.length - 1) * lineH) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, canvas.width / 2, startY + i * lineH);
    });
  } else {
    ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 1);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  return new THREE.Mesh(
    new THREE.PlaneGeometry(opts.width, opts.height),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    }),
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
